import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

/**
 * Hook to fetch rollup information for a parent entity
 * Used to determine if fields are derived and show the "Why?" breakdown
 */
export const useRollup = (entityType, entityId) => {
  const [rollup, setRollup] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [hasChildren, setHasChildren] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRollup = useCallback(async () => {
    if (!entityType || !entityId || entityType === 'task') {
      setHasChildren(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Fetch rollup data
      const res = await api.getRollup(entityType, entityId);
      setRollup(res.data.rollup);
      setHasChildren(res.data.has_children);
    } catch (err) {
      console.error('Failed to fetch rollup:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  const fetchBreakdown = useCallback(async () => {
    if (!entityType || !entityId || entityType === 'task') {
      return;
    }

    try {
      const res = await api.getRollupWhy(entityType, entityId);
      setBreakdown(res.data);
    } catch (err) {
      console.error('Failed to fetch breakdown:', err);
    }
  }, [entityType, entityId]);

  useEffect(() => {
    fetchRollup();
  }, [fetchRollup]);

  // Derived field check
  const isDerivedField = useCallback((fieldName) => {
    if (entityType === 'task') return false;
    if (!hasChildren) return false;
    return ['status', 'start_date', 'due_date', 'revised_date'].includes(fieldName);
  }, [entityType, hasChildren]);

  return {
    rollup,
    breakdown,
    hasChildren,
    loading,
    error,
    fetchRollup,
    fetchBreakdown,
    isDerivedField
  };
};

/**
 * Derived Field Lock component
 * Shows a lock icon and tooltip when field is derived from children
 */
export const DerivedFieldLock = ({ fieldName, onClick }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="ml-2 p-1 text-[var(--clr-text-muted)] hover:text-[var(--clr-brand-primary)] transition-colors"
      title={`${fieldName} is derived from child tasks. Click to see breakdown.`}
    >
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    </button>
  );
};

export default useRollup;
